// Web-specific permission handling using browser APIs
export type PermissionStatus = 'granted' | 'denied' | 'blocked' | 'unavailable' | 'limited';

export const RESULTS = {
  GRANTED: 'granted' as PermissionStatus,
  DENIED: 'denied' as PermissionStatus,
  BLOCKED: 'blocked' as PermissionStatus,
  UNAVAILABLE: 'unavailable' as PermissionStatus,
  LIMITED: 'limited' as PermissionStatus,
};

export async function checkMicrophonePermission(): Promise<PermissionStatus> {
  try {
    // Check if browser supports permissions API
    if (!navigator.permissions || !navigator.permissions.query) {
      return RESULTS.UNAVAILABLE;
    }

    const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    
    switch (permissionStatus.state) {
      case 'granted':
        return RESULTS.GRANTED;
      case 'denied':
        return RESULTS.DENIED;
      case 'prompt':
        return RESULTS.DENIED; // Not yet granted
      default:
        return RESULTS.UNAVAILABLE;
    }
  } catch (error) {
    console.warn('Error checking microphone permission:', error);
    return RESULTS.UNAVAILABLE;
  }
}

export async function requestMicrophonePermission(): Promise<PermissionStatus> {
  try {
    // Check if browser supports getUserMedia
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return RESULTS.UNAVAILABLE;
    }

    // Request microphone access
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    
    // Stop the stream immediately as we only need the permission
    stream.getTracks().forEach(track => track.stop());
    
    return RESULTS.GRANTED;
  } catch (error: any) {
    console.warn('Error requesting microphone permission:', error);
    
    // Handle specific error types
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      return RESULTS.DENIED;
    }
    if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      return RESULTS.UNAVAILABLE;
    }
    
    return RESULTS.DENIED;
  }
}

export async function startRecordingWithPermission(): Promise<boolean> {
  try {
    // Check if browser supports getUserMedia
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.warn('Browser does not support audio recording');
      return false;
    }

    // Request microphone access (this will prompt the user if needed)
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    
    // Stop the stream as we'll start a new one when actually recording
    stream.getTracks().forEach(track => track.stop());
    
    return true;
  } catch (error: any) {
    console.error('Error requesting microphone permission:', error);
    
    // Log specific error details for debugging
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      console.warn('Microphone permission denied by user');
    } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      console.warn('No microphone found');
    }
    
    return false;
  }
}